import React, { useState, useEffect, useRef } from 'react';

const FlowerPetals = () => {
  const [petals, setPetals] = useState([]);
  const animationRef = useRef(null);
  const containerRef = useRef(null);

  // Petal colors - soft, floral tones
  const petalColors = [
    '#ffb7c5', // light pink
    '#ffc8dd', // softer pink
    '#ffe5ec', // very light pink
    '#ffd1dc', // classic pink
    '#fbcce7', // lavender pink
    '#fff0f5', // lavender blush
    '#fae1f6', // light purple
  ];

  // Different petal shapes - SVG paths
  const petalShapes = [
    "M0,0 C10,-10 20,-10 30,0 C20,10 10,10 0,0", // Simple oval petal
    "M0,0 C5,-15 25,-15 30,0 C25,15 5,15 0,0", // Elongated petal
    "M0,0 C10,-10 30,-5 25,5 C20,15 5,10 0,0", // Curved petal
    "M0,0 C5,-5 15,-10 25,-5 C30,0 25,10 15,15 C5,10 0,5 0,0", // Rounded petal
  ];

  // Create a new petal
  const createPetal = () => {
    const containerWidth = containerRef.current?.clientWidth || window.innerWidth;
    return {
      id: Math.random().toString(36).substr(2, 9),
      x: Math.random() * containerWidth,
      y: -50,
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 2,
      size: 15 + Math.random() * 20,
      speedY: 1 + Math.random() * 1,
      speedX: (Math.random() - 0.5) * 1,
      color: petalColors[Math.floor(Math.random() * petalColors.length)],
      shape: petalShapes[Math.floor(Math.random() * petalShapes.length)],
      opacity: 0.7 + Math.random() * 0.3,
    };
  };

  // Animation loop
  const animate = () => {
    setPetals(prevPetals => {
      const containerHeight = containerRef.current?.clientHeight || window.innerHeight;
      const containerWidth = containerRef.current?.clientWidth || window.innerWidth;
      
      // Update existing petals
      const updatedPetals = prevPetals.map(petal => {
        // Move petal
        const newY = petal.y + petal.speedY;
        const newX = petal.x + petal.speedX;
        const newRotation = petal.rotation + petal.rotationSpeed;
        
        // Remove petals that are out of the screen
        if (newY > containerHeight + 50 || newX < -50 || newX > containerWidth + 50) {
          return null;
        }
        
        return {
          ...petal,
          y: newY,
          x: newX,
          rotation: newRotation
        };
      }).filter(Boolean);
      
      // Occasionally add new petals
      if (Math.random() < 0.1 && updatedPetals.length < 20) {
        updatedPetals.push(createPetal());
      }
      
      return updatedPetals;
    });
    
    animationRef.current = requestAnimationFrame(animate);
  };

  // Initialize animation
  useEffect(() => {
    // Create some initial petals
    const initialPetals = Array.from({ length: 15 }, createPetal);
    setPetals(initialPetals);
    
    // Start animation loop
    animationRef.current = requestAnimationFrame(animate);
    
    // Cleanup function
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div 
      ref={containerRef}
      className="flower-petals-container"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 999,
        overflow: 'hidden'
      }}
    >
      {petals.map(petal => (
        <div
          key={petal.id}
          style={{
            position: 'absolute',
            left: `${petal.x}px`,
            top: `${petal.y}px`,
            transform: `rotate(${petal.rotation}deg)`,
            width: `${petal.size}px`,
            height: `${petal.size}px`,
            opacity: petal.opacity,
            transition: 'transform 0.1s linear'
          }}
        >
          <svg 
            width={petal.size} 
            height={petal.size} 
            viewBox="0 0 30 15" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path 
              d={petal.shape} 
              fill={petal.color} 
              stroke="rgba(255,255,255,0.3)"
              strokeWidth="0.5"
            />
          </svg>
        </div>
      ))}
    </div>
  );
};

export default FlowerPetals;
